import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  ArrowCounterClockwise,
  MagnifyingGlass,
  ArrowClockwise,
  ArrowSquareOut,
  Warning,
  FloppyDisk,
  Check,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtKg = (n) => `${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })}`;
const fmtFecha = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = (iso || "").split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

const ESTADO_LABEL = {
  reprogramar: { label: "Reprogramar", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  sin_confirmar: { label: "Sin confirmar", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  confirmado: { label: "Confirmado", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  en_produccion: { label: "En producción", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  listo_para_facturar: { label: "Listo p/facturar", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  terminado: { label: "Terminado", cls: "bg-neutral-200 text-neutral-800 border-neutral-300" },
};

/** Motivo editable con guardado explícito (evita PUTs por cada tecla) */
function MotivoEditor({ pedidoId, initial, onSaved }) {
  const [value, setValue] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const originalRef = useRef(initial || "");

  useEffect(() => {
    setValue(initial || "");
    originalRef.current = initial || "";
    setDirty(false);
  }, [initial, pedidoId]);

  const guardar = useCallback(async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.put(`/dashboard/pedidos-reprogramados/${pedidoId}/motivo`, { motivo: value });
      originalRef.current = value;
      setDirty(false);
      onSaved?.(pedidoId, value);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el motivo");
    } finally { setSaving(false); }
  }, [pedidoId, value, dirty, onSaved]);

  return (
    <div className="flex items-start gap-1">
      <textarea
        value={value}
        onChange={(e) => { setValue(e.target.value); setDirty(e.target.value !== originalRef.current); }}
        onBlur={() => { if (dirty) guardar(); }}
        placeholder="Motivo de la reprogramación · ajustes a hacer…"
        rows={2}
        maxLength={500}
        className="flex-1 text-[11px] font-mono border border-neutral-200 rounded-sm px-2 py-1 focus:outline-none focus:border-[#6B1F24] resize-none"
        data-testid={`motivo-textarea-${pedidoId}`}
      />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={!dirty || saving}
        onClick={guardar}
        className="h-7 px-2 text-emerald-700 hover:bg-emerald-50 disabled:opacity-30"
        title={dirty ? "Guardar motivo" : "Sin cambios"}
        data-testid={`motivo-save-${pedidoId}`}
      >
        {saving ? <ArrowClockwise size={13} className="animate-spin" /> : dirty ? <FloppyDisk size={13} weight="bold" /> : <Check size={13} weight="bold" className="text-neutral-400" />}
      </Button>
    </div>
  );
}

export default function PedidosReprogramadosCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [soloSinMotivo, setSoloSinMotivo] = useState(false);
  const [dias, setDias] = useState(180);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/pedidos-reprogramados", {
        params: { solo_pendientes: soloPendientes, dias },
      });
      setData(data);
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [soloPendientes, dias]);
  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    const arr = data?.pedidos || [];
    const s = q.trim().toLowerCase();
    let out = arr;
    if (soloSinMotivo) out = out.filter((p) => !(p.motivo_reprogramacion || "").trim());
    if (!s) return out;
    return out.filter((p) =>
      String(p.numero || "").includes(s) ||
      (p.cliente_nombre || "").toLowerCase().includes(s) ||
      (p.origen_label || "").toLowerCase().includes(s) ||
      (p.items || []).some((it) => (it.name || "").toLowerCase().includes(s))
    );
  }, [data, q, soloSinMotivo]);

  const handleMotivoSaved = (pedidoId, motivo) => {
    setData((prev) => prev ? {
      ...prev,
      pedidos: prev.pedidos.map((p) => p.id === pedidoId ? { ...p, motivo_reprogramacion: motivo } : p),
      sin_motivo: prev.pedidos.map((p) => p.id === pedidoId ? { ...p, motivo_reprogramacion: motivo } : p)
        .filter((p) => !(p.motivo_reprogramacion || "").trim()).length,
    } : prev);
    toast.success("Motivo guardado");
  };

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="dash-pedidos-reprogramados">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <ArrowCounterClockwise size={16} weight="bold" className="text-[#6B1F24]" />
            Pedidos Reprogramados · Motivos y Ajustes
          </h2>
          <div className="text-[11px] text-[#666] font-mono mt-0.5">
            {data ? (
              <>
                {data.total_pedidos} pedido{data.total_pedidos === 1 ? "" : "s"} · {data.total_items} ítem{data.total_items === 1 ? "" : "s"} · {fmtKg(data.total_kilos)} kg reprogramados · últimos {data.dias} días
                {data.sin_motivo > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-amber-800 font-bold">
                    <Warning size={11} weight="fill" /> {data.sin_motivo} sin motivo
                  </span>
                )}
              </>
            ) : "Cargando…"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar cliente, número, origen o producto…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 w-64 text-[12px]"
              data-testid="reprog-search"
            />
          </div>
          <label className="text-[10px] uppercase font-mono text-neutral-500">Ventana</label>
          <select
            value={dias}
            onChange={(e) => setDias(parseInt(e.target.value, 10))}
            className="h-8 text-[12px] font-mono border border-neutral-300 rounded-sm px-2 bg-white focus:outline-none focus:border-[#6B1F24]"
            data-testid="reprog-ventana"
          >
            <option value={30}>30 días</option>
            <option value={90}>90 días</option>
            <option value={180}>180 días</option>
            <option value={365}>365 días</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSoloPendientes((v) => !v)}
            className={`h-8 rounded-sm text-[11px] ${soloPendientes ? "bg-[#6B1F24] text-white border-[#6B1F24] hover:bg-[#5b1a20]" : ""}`}
            data-testid="reprog-solo-pendientes"
          >
            {soloPendientes ? "Ver todos" : "Sin facturar"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSoloSinMotivo((v) => !v)}
            className={`h-8 rounded-sm text-[11px] ${soloSinMotivo ? "bg-amber-700 text-white border-amber-700 hover:bg-amber-800" : ""}`}
            data-testid="reprog-solo-sin-motivo"
          >
            {soloSinMotivo ? "Ver todos" : "Sin motivo"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={cargar}
            disabled={loading}
            className="h-8 rounded-sm text-[11px]"
            data-testid="reprog-reload"
            title="Recargar"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      {loading && (
        <div className="p-8 text-center text-neutral-500 text-sm">Cargando…</div>
      )}
      {!loading && (!data || filtered.length === 0) && (
        <div className="p-8 text-center text-neutral-400 text-sm">
          {data && (data.total_pedidos > 0)
            ? "Sin pedidos que coincidan con los filtros aplicados."
            : "Sin pedidos reprogramados en la ventana."}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left w-28">Pedido</th>
                <th className="px-3 py-2 text-left">Cliente / Origen</th>
                <th className="px-3 py-2 text-left">Productos reprogramados</th>
                <th className="px-3 py-2 text-right w-24">Kg totales</th>
                <th className="px-3 py-2 text-center w-24">Estado</th>
                <th className="px-3 py-2 text-left w-96">Motivo · ajustes</th>
                <th className="px-3 py-2 text-center w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const est = ESTADO_LABEL[p.estado_local] || { label: p.estado_local || "—", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" };
                const sinMotivo = !(p.motivo_reprogramacion || "").trim();
                return (
                  <tr key={p.id} className={`border-t border-neutral-100 align-top ${sinMotivo ? "bg-amber-50/30" : "hover:bg-neutral-50/60"}`}
                      data-testid={`reprog-row-${p.id}`}>
                    <td className="px-3 py-2 font-mono">
                      <div className="font-bold text-[#6B1F24]">MAN#{p.numero}</div>
                      <div className="text-[10px] text-neutral-400">{fmtFecha(p.fecha)}</div>
                      {p.factura_numero && (
                        <div className="text-[10px] text-emerald-700 font-bold mt-0.5">
                          Facturado {p.factura_numero}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium truncate max-w-[220px]" title={p.cliente_nombre}>
                        {p.cliente_nombre || "Sin cliente"}
                      </div>
                      {p.origen_label && (
                        <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                          desde{" "}
                          <span className="font-bold text-neutral-700">{p.origen_label}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <ul className="space-y-0.5">
                        {(p.items || []).map((it, j) => (
                          <li key={j} className="flex items-center gap-2 text-[11px]">
                            <span className="font-mono font-bold text-neutral-700 min-w-[70px] text-right">
                              {fmtKg(it.kilos)} kg
                            </span>
                            <span className="truncate flex-1" title={it.name}>{it.name}</span>
                            {it.sku && <span className="text-[9px] text-neutral-400 font-mono">{it.sku}</span>}
                            <span className="text-[10px] font-mono text-neutral-500">{fmtMoney(it.subtotal)}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#6B1F24]">
                      {fmtKg(p.kilos_total)} kg
                      <div className="text-[10px] font-mono text-neutral-500 font-normal">{fmtMoney(p.total)}</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[9px] font-bold uppercase border ${est.cls}`}>
                        {est.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <MotivoEditor
                        pedidoId={p.id}
                        initial={p.motivo_reprogramacion || ""}
                        onSaved={handleMotivoSaved}
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Link
                        to={`/pedidos?highlight=${p.id}`}
                        className="text-sky-700 hover:text-sky-900 inline-flex"
                        title="Abrir el pedido"
                        data-testid={`reprog-open-${p.id}`}
                      >
                        <ArrowSquareOut size={14} weight="bold" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
